import { useEffect, useState, useRef } from "react";
import { CreditCard, Check, X, Sparkles, Info, Droplets, Zap, Film, Image as ImageIcon, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { CinematicStage, StageHeader, GlassCard, Pill, Spinner, cx } from "@/components/shared";

const ORDER = ["starter", "creator", "pro", "studio", "production"];

export default function Membership() {
  const [packs, setPacks] = useState(null);
  const [bal, setBal] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [checkout, setCheckout] = useState(null);
  const [paying, setPaying] = useState(false);
  const [payMsg, setPayMsg] = useState(null);
  const payHandledRef = useRef(false);

  useEffect(() => {
    api.get("/packs").then(({ data }) => setPacks(data.packs)).catch(() => {});
    api.get("/credits/balance").then(({ data }) => setBal(data)).catch(() => {});
    api.get("/credits/ledger?limit=8").then(({ data }) => setLedger(data.entries || [])).catch(() => {});
  }, []);

  useEffect(() => { setPayMsg(null); setPaying(false); }, [checkout?.id]);

  // Razorpay Checkout.js is loaded lazily, only when a purchase is actually
  // attempted -- not on every page load. window.Razorpay is the loader's own
  // signal that it is already present, so a second attempt never injects the
  // script twice.
  function loadRazorpayCheckout() {
    if (window.Razorpay) return Promise.resolve();
    if (loadRazorpayCheckout._pending) return loadRazorpayCheckout._pending;
    loadRazorpayCheckout._pending = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Could not load Razorpay Checkout. Check your connection and try again."));
      document.body.appendChild(script);
    });
    return loadRazorpayCheckout._pending;
  }

  async function refreshAfterPurchase() {
    const [{ data: b }, { data: l }] = await Promise.all([
      api.get("/credits/balance"),
      api.get("/credits/ledger?limit=8"),
    ]);
    setBal(b);
    setLedger(l.entries || []);
  }

  // Vyom's own /payments/mine is the source of truth for whether an order
  // actually settled -- used to reconcile a payment whose /verify response
  // never arrived (see confirmPayment below), never to grant credits itself.
  async function pollForPaidOrder(orderId, attempts = 5, delayMs = 2000) {
    for (let i = 0; i < attempts; i++) {
      await new Promise((r) => setTimeout(r, delayMs));
      try {
        const { data } = await api.get("/payments/mine");
        const row = (data.payments || []).find((p) => p.order_id === orderId);
        if (row?.status === "paid") return true;
      } catch {
        // transient read failure -- not a reason to stop polling
      }
    }
    return false;
  }

  // Confirms a payment Razorpay's Checkout already reports as successful
  // against Vyom's own server-authoritative /payments/verify (HMAC-checked,
  // idempotent credit grant). If that call itself does not complete -- a
  // network drop, a timeout, anything short of the backend explicitly saying
  // no -- this does NOT report the payment as failed: the backend stays
  // authoritative and Razorpay's webhook grants credits independently of
  // whether this response ever arrives (proved live in
  // VYOM_RAZORPAY_READINESS_REPORT.md). Only an explicit 400/404 from
  // /verify -- a real "no", not a dropped connection -- is shown as a
  // failure.
  async function confirmPayment(orderId, paymentId, signature) {
    try {
      await api.post("/payments/verify", { order_id: orderId, payment_id: paymentId, signature });
      await refreshAfterPurchase();
      setPayMsg("Payment confirmed — credits added.");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 400 || status === 404) {
        setPayMsg("We could not verify this payment. If you were charged, it has not been credited yet — contact support with the order ID before retrying.");
      } else {
        setPayMsg("Confirming your payment with Razorpay…");
        const reconciled = await pollForPaidOrder(orderId);
        if (reconciled) {
          await refreshAfterPurchase();
          setPayMsg("Payment confirmed — credits added.");
        } else {
          setPayMsg("Still confirming this payment — it can take a minute to settle. Check your balance shortly; you will not be charged again by leaving this open.");
        }
      }
    } finally {
      setPaying(false);
    }
  }

  async function handlePay() {
    if (paying) return; // guards a double-click into a second order
    setPaying(true);
    setPayMsg(null);
    payHandledRef.current = false;
    try {
      await loadRazorpayCheckout();
      // Server looks pack_id up in its own CREDIT_PACKS and computes the
      // amount itself -- pack_id is the only thing this request sends.
      const { data: order } = await api.post("/payments/order", { pack_id: checkout.id });
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount_inr * 100,
        currency: order.currency,
        order_id: order.order_id,
        name: "Vyom Studio",
        description: `${order.pack} pack`,
        handler: (response) => {
          if (payHandledRef.current) return; // duplicate success-callback guard
          payHandledRef.current = true;
          setPaying(true);
          confirmPayment(response.razorpay_order_id, response.razorpay_payment_id, response.razorpay_signature);
        },
        modal: {
          ondismiss: () => {
            // User closed the widget without paying -- nothing was sent to
            // /verify, nothing was charged, no credits to grant.
            setPaying(false);
          },
        },
        notes: { pack_id: checkout.id },
      });
      rzp.on("payment.failed", (resp) => {
        // Razorpay itself declined the payment -- it never reaches /verify,
        // so nothing is granted. Vyom's webhook independently records this
        // same failure server-side (proved live in the readiness report).
        setPayMsg(resp?.error?.description || "Payment failed. No credits were charged.");
        setPaying(false);
      });
      rzp.open();
    } catch (err) {
      setPayMsg(err?.response?.data?.detail?.message || err?.message || "Could not start checkout. Please try again.");
      setPaying(false);
    }
  }

  if (!packs) return <Spinner label="Loading membership…" />;

  return (
    <CinematicStage>
      <StageHeader stage="MEMBERSHIP" room="Studio Control · Membership" icon={CreditCard}
        title="Buy credits, produce films"
        subtitle="Vyom credits are consumed when AI production runs — the studio calls the providers on your behalf with server-side keys. No subscription, no free AI: you buy exactly the production capacity you need." />

      {/* Balance strip */}
      <GlassCard className="p-5 mb-6 flex flex-wrap items-center gap-4" data-testid="credit-summary">
        <span className="grid place-items-center w-12 h-12 rounded-xl bg-[#FFB300]/12 border border-[#FFB300]/30 text-[#FFB300]"><Sparkles className="w-6 h-6" /></span>
        <div>
          <div className="font-display text-3xl font-bold text-white">{bal ? bal.balance.toLocaleString() : "—"} <span className="text-sm text-slate-500 font-normal">credits</span></div>
          <div className="text-xs text-slate-500">{bal?.watermark_free ? "Watermark removed" : "Vyom watermark on exports"} · {bal ? bal.reserved.toLocaleString() : 0} reserved</div>
        </div>
        <div className="ml-auto text-xs text-slate-500 max-w-xs">Credits are an internal Vyom entitlement — not provider credits. Provider keys always stay server-side.</div>
      </GlassCard>

      {/* Packs */}
      <div className="grid sm:grid-cols-2 xl:grid-cols-5 gap-4" data-testid="packs-grid">
        {ORDER.map((id) => {
          const p = packs[id];
          const ex = p.examples || {};
          return (
            <GlassCard key={id} data-testid={`pack-${id}`}
              className={cx("p-5 relative flex flex-col", p.popular && "border-[#5B4DFF]/50 shadow-[0_0_30px_-8px_rgba(91,77,255,0.5)]")}>
              {p.popular && <span className="absolute -top-3 left-5"><Pill tone="purple">Most popular</Pill></span>}
              <div className="font-display text-lg font-semibold text-white">{p.name}</div>
              <div className="mt-3 flex items-end gap-1">
                <span className="font-display text-3xl font-bold text-white">₹{p.price_inr.toLocaleString()}</span>
              </div>
              <div className="mt-1 text-[#FFB300] text-sm font-semibold flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> {p.credits.toLocaleString()} credits</div>
              <ul className="mt-4 space-y-2 flex-1 text-xs text-slate-400">
                <li className="flex items-center gap-1.5"><Film className="w-3.5 h-3.5 text-[#b6aaff]" /> ~{ex.veo_fast_1080_8s} Veo Fast 1080p·8s</li>
                <li className="flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-[#b6aaff]" /> ~{ex.veo_lite_720_8s} Veo Lite 720p·8s</li>
                <li className="flex items-center gap-1.5"><ImageIcon className="w-3.5 h-3.5 text-[#b6aaff]" /> ~{ex.images} images</li>
                <li className="flex items-center gap-1.5">{p.watermark_free
                  ? <><Check className="w-3.5 h-3.5 text-emerald-400" /> No watermark</>
                  : <><Droplets className="w-3.5 h-3.5 text-amber-400" /> Vyom watermark</>}</li>
              </ul>
              <button onClick={() => setCheckout({ id, ...p })} data-testid={`buy-${id}`}
                className={cx("mt-5 w-full py-2.5 rounded-xl font-semibold text-sm transition",
                  p.popular ? "bg-gradient-to-r from-[#5B4DFF] to-[#7C70FF] text-white glow-purple"
                  : "bg-white/8 border border-white/12 text-white hover:border-[#5B4DFF]/50")}>
                Buy credits
              </button>
            </GlassCard>
          );
        })}
      </div>
      <p className="text-xs text-slate-500 mt-3">Example counts are indicative — actual credit use depends on the model, resolution and duration you choose (see the live rate card in Video Engine).</p>

      {/* Watermark policy */}
      <GlassCard className="p-6 mt-8" data-testid="watermark-table">
        <div className="flex items-center gap-2 mb-4"><Droplets className="w-4 h-4 text-[#b6aaff]" /><h3 className="font-display text-lg font-semibold">Vyom watermark policy</h3></div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {ORDER.map((id) => (
            <div key={id} className="rounded-xl bg-white/[0.04] border border-white/10 p-3 text-center">
              <div className="text-xs text-slate-400 mb-1">{packs[id].name}</div>
              {packs[id].watermark_free
                ? <Pill tone="green" className="justify-center w-full">Removed</Pill>
                : <Pill tone="amber" className="justify-center w-full">Included</Pill>}
            </div>
          ))}
        </div>
      </GlassCard>

      {/* Usage ledger */}
      <GlassCard className="p-6 mt-8" data-testid="usage-ledger">
        <div className="font-mono text-[11px] uppercase tracking-wider text-slate-500 mb-3">Recent credit activity</div>
        {ledger.length === 0 ? (
          <div className="py-8 text-center text-slate-500 text-sm">No activity yet. Credits move when you buy packs or run AI production.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {ledger.map((e, i) => (
              <div key={i} className="flex items-center justify-between py-2.5 text-sm">
                <span className="text-slate-300">{e.transaction_type} · {e.reason}</span>
                <span className={cx("font-mono", e.credits < 0 ? "text-red-300" : "text-emerald-300")}>{e.credits > 0 ? `+${e.credits}` : e.credits}</span>
              </div>
            ))}
          </div>
        )}
      </GlassCard>

      <div className="mt-6 flex items-start gap-2.5 text-xs text-slate-500 bg-white/[0.03] border border-white/8 rounded-xl px-4 py-3" data-testid="integration-note">
        <Info className="w-4 h-4 shrink-0 mt-0.5 text-[#FFB300]" />
        <span><strong className="text-slate-300">Integration point:</strong> balances, packs and the ledger are live from the backend. Checkout runs through Razorpay Test Mode (create-order → hosted checkout → signature + webhook verification → credit grant) — a real Test Mode charge simulation; no real money moves.</span>
      </div>

      {checkout && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setCheckout(null)} data-testid="checkout-modal">
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#12121f] p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl font-bold text-white">{checkout.name} pack</h3>
              <button onClick={() => setCheckout(null)} data-testid="checkout-close" className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/5"><X className="w-4 h-4" /></button>
            </div>
            <div className="rounded-xl bg-white/[0.04] border border-white/10 p-4 flex items-center justify-between">
              <span className="text-slate-300">{checkout.credits.toLocaleString()} credits</span>
              <span className="font-display text-2xl font-bold text-white">₹{checkout.price_inr.toLocaleString()}</span>
            </div>
            <div className="mt-3 text-sm text-slate-400">{checkout.watermark_free ? "No watermark on exports." : "Vyom watermark on exports."} Credits never expire.</div>
            <div className="mt-4 flex items-start gap-2.5 text-sm text-slate-400"><Info className="w-4 h-4 shrink-0 mt-0.5 text-[#FFB300]" /> Payment runs through Razorpay’s secure Test Mode checkout. Credits are granted only after server-verified payment — Vyom never sees or stores your card details.</div>
            <button onClick={handlePay} disabled={paying} data-testid="checkout-pay"
              className={cx("mt-5 w-full py-3 rounded-xl font-semibold transition flex items-center justify-center gap-2",
                paying ? "bg-white/5 border border-white/10 text-slate-500 cursor-not-allowed"
                       : "bg-gradient-to-r from-[#5B4DFF] to-[#7C70FF] text-white glow-purple")}>
              {paying && <Loader2 className="w-4 h-4 animate-spin" />}
              {paying ? "Processing…" : "Pay with Razorpay"}
            </button>
            {payMsg && <div className="mt-3 text-xs text-slate-400" data-testid="checkout-status">{payMsg}</div>}
          </div>
        </div>
      )}
    </CinematicStage>
  );
}
